import * as THREE from 'three';
import { CONFIG } from '../config.js';

/**
 * Creates and manages the procedural grass terrain.
 */
export class Terrain {
    /** @type {THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>} */
    #mesh;

    constructor() {
        this.#mesh = this.#createTerrain();
    }

    /**
     * Get the terrain mesh
     * @returns {THREE.Mesh}
     */
    get mesh() {
        return this.#mesh;
    }

    /**
     * Create the terrain with procedural grass texture
     * @returns {THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>}
     */
    #createTerrain() {
        const terrainSize = CONFIG.world.terrainSize;

        // Create a large flat plane
        const geometry = new THREE.PlaneGeometry(terrainSize, terrainSize);
        geometry.rotateX(-Math.PI / 2);

        // Create procedural grass texture
        const texture = this.#createGrassTexture();
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(40, 40);

        const material = new THREE.MeshStandardMaterial({
            map: texture,
            roughness: 0.8,
            metalness: 0.1,
            side: THREE.DoubleSide
        });

        const terrain = new THREE.Mesh(geometry, material);
        terrain.receiveShadow = true;

        return terrain;
    }

    /**
     * Create a procedural grass texture on a canvas
     * @returns {THREE.CanvasTexture}
     */
    #createGrassTexture() {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const context = canvas.getContext('2d');

        if (!context) {
            throw new Error('Failed to get 2D context for grass texture');
        }

        // Base green color
        context.fillStyle = '#7CFC00';
        context.fillRect(0, 0, 256, 256);

        // Add texture details (grass blades)
        for (let i = 0; i < 1000; i++) {
            const x = Math.random() * 256;
            const y = Math.random() * 256;
            const radius = Math.random() * 2 + 1;

            // Random green variations
            const green = Math.floor(180 + Math.random() * 75);
            context.fillStyle = `rgb(0, ${green}, 0)`;

            context.beginPath();
            context.arc(x, y, radius, 0, Math.PI * 2);
            context.fill();
        }

        return new THREE.CanvasTexture(canvas);
    }

    /**
     * Dispose of terrain resources
     */
    dispose() {
        this.#mesh.geometry.dispose();

        if (Array.isArray(this.#mesh.material)) {
            this.#mesh.material.forEach(m => {
                if (m.map) m.map.dispose();
                m.dispose();
            });
        } else {
            if (this.#mesh.material.map) {
                this.#mesh.material.map.dispose();
            }
            this.#mesh.material.dispose();
        }
    }
}
